import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import DeviceAssignmentSection from "./DeviceAssignmentSection";
import { buildAccessRows, buildDestinations, buildIntents, installBlockers } from "./claimIntents";
import { selectedRecipients } from "./alertRecipients";
import { missingRequiredThresholds, submittedThresholdRows } from "./thresholds";
import { draftCustomerIdentity } from "./customerIdentity";
import { completeReviewedInstallation, fail, writeProgress } from "./finalizeInstallation";

const storageKey = (claimCode) => `rivio_claim_customer_install:${claimCode}`;

function readStored(claimCode) {
  try {
    const raw = sessionStorage.getItem(storageKey(claimCode));
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeStored(claimCode, value) {
  try {
    sessionStorage.setItem(storageKey(claimCode), JSON.stringify(value));
  } catch {
    /* storage unavailable: the saved IDs stay in memory for this page */
  }
}

export function clearClaimCustomerInstall(claimCode) {
  try {
    sessionStorage.removeItem(storageKey(claimCode));
  } catch {
    /* storage unavailable */
  }
}

function errorText(error) {
  return error?.message || error?.msg || "Request failed.";
}

function addressText(address) {
  if (!address) return "";
  return [address.street_address1, address.street_address2, address.city, [address.state, address.postal_code].filter(Boolean).join(" "), address.country]
    .map((part) => String(part || "").trim())
    .filter(Boolean)
    .join(", ");
}

function assetDetailsText(item, dropdowns) {
  const option = (dropdowns.items || []).find((row) => String(row.item_id) === String(item?.item_id));
  const parts = (option?.Attributes || [])
    .map((attr) => {
      const raw = item?.attributes?.[attr.attribute_id];
      if (raw == null || String(raw).trim() === "") return null;
      const value = attr.field_type_id === 4 ? (raw === true || raw === "true" ? "Yes" : "No") : String(raw);
      const unit = attr.unit || attr.unit_name || attr.uom || "";
      return `${attr.label || attr.attribute_name} ${value}${unit ? ` ${unit}` : ""}`;
    })
    .filter(Boolean);
  return [option?.item_name || option?.name, ...parts].filter(Boolean).join("; ");
}

function submittedItemAttributes(item) {
  return Object.entries(item?.attributes || {})
    .filter(([, value]) => value != null && String(value).trim() !== "")
    .map(([attributeId, value]) => ({ attribute_id: Number(attributeId), value: String(value).trim() }))
    .filter((row) => Number.isSafeInteger(row.attribute_id) && row.attribute_id > 0);
}

/**
 * Claim mode for the customer onboarding wizard: adds the device assignment
 * to its review and turns Submit into one "Confirm & install device" action
 * (save customer -> map the reviewed asset by client reference -> complete the
 * claim). Saved IDs are kept so a failed assignment is retried without
 * creating the customer again. Inactive (returns active:false) without a claim.
 */
export default function useClaimCustomerInstall({ claimContext, state, dropdowns, lookupsLoaded }) {
  const active = Boolean(claimContext?.session);
  const claimCode = claimContext?.claimCode;
  const stored = useMemo(() => (active ? readStored(claimCode) : null), [active, claimCode]);
  const [assetRef, setAssetRef] = useState(stored?.assetRef || "");
  const [deviceName, setDeviceName] = useState(stored?.deviceName ?? claimContext?.initialDeviceName ?? "");
  const [installationDate, setInstallationDate] = useState(stored?.installationDate || "");
  const [thresholds, setThresholds] = useState(stored?.thresholds?.length ? stored.thresholds : claimContext?.thresholds || []);
  const [alerts, setAlerts] = useState(stored?.alerts || { enabled: false, recipientKeys: [] });
  const [contactAccess, setContactAccess] = useState(stored?.contactAccess || {});
  const [saved, setSaved] = useState(stored?.saved || null);
  // Attestations are never restored as accepted.
  const [attest, setAttest] = useState({ accuracy: false, terms: false });
  const [assignmentError, setAssignmentError] = useState("");
  const [stage, setStage] = useState(null);
  const lock = useRef(false);

  // Thresholds arrive after the device overview loads.
  useEffect(() => {
    if (active && !thresholds.length && claimContext?.thresholds?.length) setThresholds(claimContext.thresholds);
  }, [active, claimContext?.thresholds]);

  useEffect(() => {
    if (!active) return;
    writeStored(claimCode, { assetRef, deviceName, installationDate, thresholds, alerts, contactAccess, saved });
  }, [active, claimCode, assetRef, deviceName, installationDate, thresholds, alerts, contactAccess, saved]);

  const items = state.items || [];
  // One new asset is preselected; several require an explicit choice here.
  useEffect(() => {
    if (!active) return;
    if (items.length === 1 && assetRef !== items[0].id) setAssetRef(items[0].id);
    else if (items.length !== 1 && assetRef && !items.some((item) => item.id === assetRef)) setAssetRef("");
  }, [active, items, assetRef]);

  const item = items.find((row) => row.id === assetRef) || null;
  const address = item ? state.addresses?.[Number(item.address_index)] : null;

  const newContacts = useMemo(
    () => (state.contacts || [])
      .filter((contact) => String(contact.contact_first_name || "").trim() || String(contact.email || "").trim())
      .map((contact) => ({
        key: contact.id,
        name: `${contact.contact_first_name || ""} ${contact.contact_last_name || ""}`.trim(),
        email: String(contact.email || "").trim() || null,
        phone: String(contact.phone || "").trim() || null,
        type: (dropdowns.contactTypes || []).find((row) => String(row.contact_type_id) === String(contact.contact_type_id))?.type_name || "",
      })),
    [state.contacts, dropdowns.contactTypes],
  );

  const createdIds = useMemo(
    () => Object.fromEntries(
      (saved?.contacts || [])
        .filter((row) => row.client_ref && row.contact_id)
        .map((row) => [row.client_ref, Number(row.contact_id)]),
    ),
    [saved],
  );

  const accessRows = useMemo(
    () => buildAccessRows({ includeExisting: false, newContacts, choices: contactAccess, createdIds }),
    [newContacts, contactAccess, createdIds],
  );
  const destinations = useMemo(() => buildDestinations({ includeExisting: false, newContacts }), [newContacts]);
  const recipientCount = selectedRecipients(alerts, destinations).length;
  const identity = draftCustomerIdentity(state.entity, dropdowns, { lookupsLoaded });
  const providerNames = (dropdowns.providers || [])
    .filter((provider) => (item?.provider_ids || []).map(Number).includes(Number(provider.provider_id)))
    .map((provider) => provider.provider_name);

  const blockers = useMemo(() => {
    if (!active) return [];
    const list = installBlockers({
      deviceName,
      installationDate,
      thresholdsMissing: missingRequiredThresholds(thresholds),
      termsVersion: claimContext.termsVersion,
      alerts,
      recipientCount,
      assetChosen: Boolean(item),
    });
    if (!items.length) list.unshift("Add the tank or asset this device is installed on in Tanks / Assets.");
    if (item && !String(item.map_identifier || "").trim()) list.push("The assigned asset needs an asset identifier. Edit it in Tanks / Assets.");
    if (item && !address) list.push("The assigned asset is not linked to a service address. Edit it in Tanks / Assets.");
    if (!identity.complete) list.push("Customer details are incomplete. Use Edit customer before confirming.");
    if (identity.unresolved.length) list.push(`Customer ${identity.unresolved.join(", ")} could not be resolved. Choose it again in Customer.`);
    if (!attest.accuracy || !attest.terms) list.push("Confirm the information is accurate and accept the installation terms.");
    return list;
  }, [active, deviceName, installationDate, thresholds, claimContext?.termsVersion, alerts, recipientCount, item, items.length, address, identity.complete, identity.unresolved, attest]);

  const assetOptions = items.map((row, index) => {
    const linked = state.addresses?.[Number(row.address_index)];
    return {
      value: row.id,
      label: `${row.name || row.map_identifier || `Asset ${index + 1}`}${linked ? ` - ${linked.name || addressText(linked)}` : ""}`,
    };
  });

  const reviewSection = active ? (
    <DeviceAssignmentSection
      deviceId={claimContext.deviceId}
      customer={identity}
      addressText={address ? [address.name, addressText(address)].filter(Boolean).join(" - ") : ""}
      assetOptions={assetOptions}
      assetValue={assetRef}
      onAssetChange={setAssetRef}
      assetIdentifier={item?.map_identifier || ""}
      assetDetails={assetDetailsText(item, dropdowns)}
      providerNames={providerNames}
      deviceName={deviceName}
      onDeviceName={setDeviceName}
      installationDate={installationDate}
      onInstallationDate={setInstallationDate}
      thresholds={thresholds}
      onThreshold={(index, patch) => setThresholds((rows) => rows.map((row, i) => (i === index ? { ...row, ...patch } : row)))}
      alerts={alerts}
      onAlerts={setAlerts}
      destinations={destinations}
      accessRows={accessRows}
      onAccess={(key, patch) => setContactAccess((current) => ({ ...current, [key]: { ...(current[key] || {}), ...patch } }))}
      attest={attest}
      onAttest={setAttest}
      termsVersion={claimContext.termsVersion}
      blockers={blockers}
      disabled={stage === "submitting"}
    />
  ) : null;

  /**
   * buildPayload(stripProviderRefs) returns the onboarding payload. The target
   * item's provider_ids are left out so sharing runs as a recorded operation.
   */
  const submit = useCallback(async ({ createCustomer, buildPayload }) => {
    if (!active || lock.current || blockers.length) return null;
    lock.current = true;
    setAssignmentError("");
    setStage("submitting");
    claimContext.onStage?.("submitting");
    let savedNow = saved;
    try {
      if (!savedNow?.entityId) {
        const response = await createCustomer(buildPayload({ stripProvidersFor: assetRef }));
        const data = response?.data || {};
        const entityId = Number(data.id ?? data.attributes?.entity_id);
        if (!Number.isSafeInteger(entityId) || entityId < 1) {
          throw fail("CREATION_RESPONSE_INVALID", "Customer creation did not return a customer ID.");
        }
        savedNow = {
          entityId,
          targets: data.installation_targets || [],
          contacts: data.created_contacts || [],
          addresses: data.created_addresses || [],
        };
        setSaved(savedNow);
        writeStored(claimCode, { assetRef, deviceName, installationDate, thresholds, alerts, contactAccess, saved: savedNow });
      }
      const target = (savedNow.targets || []).find((row) => row.client_ref && row.client_ref === assetRef);
      if (!target) {
        throw fail("ASSET_MAPPING_MISSING", "The saved customer's response did not identify the reviewed asset. Do not create the customer again; contact support or retry.", { assignment: true });
      }
      if (target.address_client_ref && address?.id && target.address_client_ref !== address.id) {
        throw fail("ASSET_ADDRESS_MISMATCH", "The saved asset is not at the reviewed service address.", { assignment: true });
      }
      const savedCreatedIds = Object.fromEntries(
        (savedNow.contacts || []).filter((row) => row.client_ref && row.contact_id).map((row) => [row.client_ref, Number(row.contact_id)]),
      );
      const rows = buildAccessRows({ includeExisting: false, newContacts, choices: contactAccess, createdIds: savedCreatedIds });
      const intents = buildIntents({
        providerIds: item?.provider_ids || [],
        accessRows: rows,
        alerts,
        destinations,
        createdIds: savedCreatedIds,
      });
      const { completed, plan } = await completeReviewedInstallation({
        api: claimContext.api,
        session: claimContext.session,
        target: { entityId: savedNow.entityId, addressId: target.address_id, assetId: target.address_item_id },
        install: {
          deviceName,
          installationDate,
          thresholds: submittedThresholdRows(thresholds),
          asset: { map_identifier: String(item.map_identifier).trim(), attributes: submittedItemAttributes(item) },
        },
        intents,
      });
      writeProgress(claimCode, { claimId: claimContext.session.claim_id });
      clearClaimCustomerInstall(claimCode);
      setStage(null);
      return { completed, plan, entityId: savedNow.entityId };
    } catch (error) {
      if (savedNow?.entityId) {
        setStage("assignment_attention");
        claimContext.onStage?.("assignment_attention");
        setAssignmentError(errorText(error));
      } else {
        setStage(null);
        claimContext.onStage?.("review");
        throw error;
      }
      return null;
    } finally {
      lock.current = false;
    }
  }, [active, blockers, saved, claimContext, claimCode, assetRef, address, newContacts, contactAccess, item, alerts, destinations, deviceName, installationDate, thresholds]);

  return {
    active,
    reviewSection,
    blockers,
    submit,
    saved,
    stage,
    assignmentError,
  };
}
